/**
 * PoolMembershipEditor — root container for the explicit-opt-in pool
 * membership UI (pool_inclusions + pool_refinements), a staged-ops editor:
 *
 *   - Header strip: pool defaults, tenancy, candidate counts, materialized
 *     revision.
 *   - Two panels side-by-side: InclusionList (persisted + staged) on the
 *     left, CandidatePicker on the right.
 *   - PushHealthPanel collapsible below.
 *   - Sticky action bar: pending-count + Discard + Save.
 *
 * The editor owns one client-side `PendingOp[]` log. Every user action
 * appends an op. One `Save` button flushes via POST /membership:apply.
 * No per-toggle PUTs, no client-side resolver work — materialized counts
 * come straight from GET /membership:materialize.
 *
 * Design-doc references:
 *   - poolMembership/SPEC.md (this component's authoritative contract)
 *   - /Users/kevin.viggers/.claude/plans/the-application-mapper-component-cozy-kernighan.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ApplyMembershipResult,
  GetCandidatesResponse,
  GetMembershipResponse,
  MaterializedEntry,
  PendingOp,
  PoolFlagDefaults,
  PoolRefinement,
  PoolTenancy,
} from '@interlucent/organization-protocol';
import styles from './PoolMembershipEditor.module.css';
import flagStyles from './poolMembership/FlagToggles.module.css';
import { CandidatePicker } from './poolMembership/CandidatePicker';
import { InclusionList } from './poolMembership/InclusionList';
import { PushHealthPanel } from './poolMembership/PushHealthPanel';
import { buildCandidateIndex, type CandidateIndex } from './poolMembership/types';
import { WARM_TARGET_MAX } from './poolMembership/warmTargetConstants';

export interface PoolMembershipEditorProps {
  poolId: string;
  /** Pass-through auth header factory. */
  authHeaders: () => Promise<Record<string, string>>;
  /** Base URL for the API. */
  apiBaseUrl: string;
  /** Called when an error occurs so the parent can surface it in a banner. */
  onError?: (message: string) => void;
  /**
   * When provided, renders a "Legacy view" link in the banner. Clicking
   * it calls this callback — the route can flip its feature flag off or
   * navigate to a URL without the flag.
   */
  onSwitchToLegacy?: () => void;
}

interface MaterializeResponse {
  materializedRevision: number;
  entries: MaterializedEntry[];
  staleInclusionIds?: string[];
  staleRefinementIds?: string[];
}

export function PoolMembershipEditor({
  poolId,
  authHeaders,
  apiBaseUrl,
  onError,
  onSwitchToLegacy,
}: PoolMembershipEditorProps) {
  // -----------------------------------------------------------------------
  // Server state
  // -----------------------------------------------------------------------
  const [membership, setMembership] = useState<GetMembershipResponse | null>(null);
  const [materialize, setMaterialize] = useState<MaterializeResponse | null>(null);
  const [candidates, setCandidates] = useState<GetCandidatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [pushHealthRefresh, setPushHealthRefresh] = useState(0);

  // -----------------------------------------------------------------------
  // Staged ops
  // -----------------------------------------------------------------------
  const [pendingOps, setPendingOps] = useState<PendingOp[]>([]);
  const [saving, setSaving] = useState(false);

  const appendOp = useCallback((op: PendingOp) => {
    setPendingOps((prev) => coalesceOps([...prev, op]));
  }, []);

  const discardAll = useCallback(() => {
    if (pendingOps.length === 0) return;
    if (!window.confirm(`Discard ${pendingOps.length} pending change${pendingOps.length === 1 ? '' : 's'}?`)) return;
    setPendingOps([]);
  }, [pendingOps.length]);

  const dropStagedAdd = useCallback((stagedIndex: number) => {
    setPendingOps((prev) => {
      const targetOp = prev[stagedIndex];
      if (!targetOp || targetOp.kind !== 'addInclusion') return prev;
      const targetId = `staged-${stagedIndex}`;
      // Drop the add + any update/remove ops keyed off the same staged id.
      return prev.filter((op, i) => {
        if (i === stagedIndex) return false;
        if (op.kind === 'updateInclusion' && op.id === targetId) return false;
        if (op.kind === 'removeInclusion' && op.id === targetId) return false;
        return true;
      });
    });
  }, []);

  const undoRemoveInclusion = useCallback((id: string) => {
    setPendingOps((prev) => prev.filter((op) => !(op.kind === 'removeInclusion' && op.id === id)));
  }, []);

  const undoUpdateInclusion = useCallback((id: string) => {
    setPendingOps((prev) => prev.filter((op) => !(op.kind === 'updateInclusion' && op.id === id)));
  }, []);

  // -----------------------------------------------------------------------
  // Fetch — all three in parallel on mount (and after save).
  // -----------------------------------------------------------------------
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const base = `${apiBaseUrl}/api/admin/pools/${poolId}`;
      const [mRes, matRes, candRes] = await Promise.all([
        fetch(`${base}/membership`, { headers }),
        fetch(`${base}/membership:materialize`, { headers }),
        fetch(`${base}/membership:candidates`, { headers }),
      ]);
      if (!mRes.ok) throw new Error(`GET /membership failed: HTTP ${mRes.status}`);
      if (!matRes.ok) throw new Error(`GET /membership:materialize failed: HTTP ${matRes.status}`);
      if (!candRes.ok) throw new Error(`GET /membership:candidates failed: HTTP ${candRes.status}`);
      const [m, mat, cand] = (await Promise.all([mRes.json(), matRes.json(), candRes.json()])) as [
        GetMembershipResponse,
        MaterializeResponse,
        GetCandidatesResponse,
      ];
      setMembership(m);
      setMaterialize(mat);
      setCandidates(cand);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [poolId, authHeaders, apiBaseUrl, onError]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // -----------------------------------------------------------------------
  // Save — one POST /membership:apply call flushes every pending op.
  // -----------------------------------------------------------------------
  const handleSave = useCallback(async () => {
    if (pendingOps.length === 0) return;
    setSaving(true);
    try {
      const headers = await authHeaders();
      // Normalize: the server doesn't know about our staged-<n> ids. Those
      // rows are brand-new to it, so addInclusion/addRefinement stay with
      // their current payloads. update/remove ops against staged-<n> ids
      // only matter locally — we fold them into the add op's payload here
      // so the server receives the final state in a single op.
      const normalized = foldStagedOps(pendingOps);
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/membership:apply`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ops: normalized }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`POST /membership:apply failed: HTTP ${res.status} ${body}`);
      }
      const result = (await res.json()) as ApplyMembershipResult;
      if (!result.ok) {
        const failed = result.results.filter((r) => !r.ok);
        const detail = failed.map((r) => `op[${r.opIndex}]: ${r.error ?? 'unknown error'}`).join('\n');
        onError?.(`Some ops failed to apply:\n${detail}`);
      }
      setPendingOps([]);
      await fetchAll();
      setPushHealthRefresh((n) => n + 1);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [pendingOps, poolId, authHeaders, apiBaseUrl, onError, fetchAll]);

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------
  const candidateIndex: CandidateIndex = useMemo(() => {
    if (!candidates) return { byKey: new Map(), counts: { orgs: 0, projects: 0, apps: 0, versions: 0 } };
    return buildCandidateIndex(candidates);
  }, [candidates]);

  const staleInclusionIdSet = useMemo(() => new Set(materialize?.staleInclusionIds ?? []), [materialize]);
  const staleRefinementIdSet = useMemo(() => new Set(materialize?.staleRefinementIds ?? []), [materialize]);

  /**
   * Pool defaults layered with any staged updatePoolDefaults ops so the
   * header reflects prospective state immediately. Pool defaults have no
   * "inherit" semantic — they're the base layer resolvers chain up from.
   */
  const effectivePoolDefaults: PoolFlagDefaults = useMemo(() => {
    const base: PoolFlagDefaults =
      membership?.pool.defaults ?? { precache: false, prelaunch: false, warmTarget: 0, multiplex: false };
    let result = { ...base };
    for (const op of pendingOps) {
      if (op.kind === 'updatePoolDefaults') {
        if (op.payload.precache !== undefined) result.precache = op.payload.precache;
        if (op.payload.prelaunch !== undefined) result.prelaunch = op.payload.prelaunch;
        if (op.payload.warmTarget !== undefined) {
          result.warmTarget = op.payload.warmTarget;
          // Mirror the gateway-side dual-write so the chip's effective state
          // is consistent with the legacy boolean (PR 5 drops this).
          result.prelaunch = op.payload.warmTarget > 0;
        }
        if (op.payload.multiplex !== undefined) result.multiplex = op.payload.multiplex;
      }
    }
    return result;
  }, [membership, pendingOps]);

  const togglePoolDefaultBool = useCallback((field: 'precache' | 'multiplex') => {
    const current = effectivePoolDefaults[field];
    appendOp({ kind: 'updatePoolDefaults', payload: { [field]: !current } });
  }, [effectivePoolDefaults, appendOp]);

  const setPoolDefaultWarmTarget = useCallback((next: number) => {
    appendOp({ kind: 'updatePoolDefaults', payload: { warmTarget: next } });
  }, [appendOp]);

  const poolDefaultsDirty = useMemo(() => {
    if (!membership) return false;
    return effectivePoolDefaults.precache !== membership.pool.defaults.precache
      || effectivePoolDefaults.warmTarget !== membership.pool.defaults.warmTarget
      || effectivePoolDefaults.multiplex !== membership.pool.defaults.multiplex;
  }, [effectivePoolDefaults, membership]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (loading && !membership) {
    return <div className={styles.message}>Loading membership…</div>;
  }
  if (!membership || !materialize || !candidates) {
    return <div className={styles.message}>Membership unavailable.</div>;
  }

  const poolDefaults: PoolFlagDefaults = effectivePoolDefaults;
  const tenancy: PoolTenancy = membership.pool.tenancy;
  const hasPending = pendingOps.length > 0;

  return (
    <div className={styles.container}>
      {onSwitchToLegacy && (
        <div className={styles.legacyBanner}>
          <span className={styles.legacyBannerText}>
            New membership editor · opt-in preview. The legacy override tree is still available.
          </span>
          <button type="button" className={styles.legacyBannerLink} onClick={onSwitchToLegacy}>
            Legacy view
          </button>
        </div>
      )}

      <WarmTargetMigrationBanner poolId={membership.pool.id} />

      {!membership.pool.active && (
        <div className={styles.message}>
          <span className={styles.inactiveWarning}>
            Pool is inactive. Materialized output will stay empty until it&rsquo;s re-activated.
          </span>
        </div>
      )}

      <div className={styles.headerStrip}>
        <div className={styles.headerCell}>
          <span className={styles.headerLabel}>
            Pool defaults{poolDefaultsDirty ? ' · modified' : ''}
          </span>
          <div className={styles.flagSummary}>
            <button
              type="button"
              className={`${styles.flagSummaryChip} ${poolDefaults.precache ? styles.flagSummaryOn : styles.flagSummaryOff}`}
              title={`Toggle pool default precache. Currently ${poolDefaults.precache ? 'on' : 'off'}.`}
              onClick={() => togglePoolDefaultBool('precache')}
            >
              precache {poolDefaults.precache ? 'on' : 'off'}
            </button>
            <PoolDefaultWarmTargetChip
              value={poolDefaults.warmTarget}
              onChange={setPoolDefaultWarmTarget}
            />
            <button
              type="button"
              className={`${styles.flagSummaryChip} ${poolDefaults.multiplex ? styles.flagSummaryOn : styles.flagSummaryOff}`}
              title={`Toggle pool default multiplex. Currently ${poolDefaults.multiplex ? 'on' : 'off'}.`}
              onClick={() => togglePoolDefaultBool('multiplex')}
            >
              multiplex {poolDefaults.multiplex ? 'on' : 'off'}
            </button>
          </div>
          <span className={styles.headerLabel} style={{ color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>
            Inherited cells in each inclusion resolve to these values. Click to stage a change.
          </span>
        </div>
        <div className={styles.headerCell}>
          <span className={styles.headerLabel}>Tenancy</span>
          <span className={styles.tenancyPill} title={tenancyHint(tenancy)}>{tenancy}</span>
          <span className={styles.headerLabel} style={{ color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>
            {tenancyHint(tenancy)}
          </span>
        </div>
        <div className={styles.headerCell}>
          <span className={styles.headerLabel}>Eligible candidates</span>
          <span className={styles.headerValue}>
            {candidateIndex.counts.projects}p · {candidateIndex.counts.apps}a · {candidateIndex.counts.versions}v
          </span>
          <span className={styles.headerLabel} style={{ color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>
            Across {candidateIndex.counts.orgs} org{candidateIndex.counts.orgs === 1 ? '' : 's'}.
          </span>
        </div>
        <div className={styles.headerCell}>
          <span className={styles.headerLabel}>Materialized</span>
          <span className={styles.headerValue}>rev {materialize.materializedRevision}</span>
          <span className={styles.headerLabel} style={{ color: 'var(--color-text-tertiary)', fontWeight: 'normal' }}>
            {materialize.entries.length} entr{materialize.entries.length === 1 ? 'y' : 'ies'} provisioned.
          </span>
        </div>
      </div>

      <div className={styles.panels}>
        <InclusionList
          poolId={poolId}
          inclusions={membership.inclusions}
          refinements={membership.refinements.filter((r) => !staleRefinementIdSet.has(r.id))}
          entries={materialize.entries}
          staleInclusionIds={staleInclusionIdSet}
          pendingOps={pendingOps}
          poolDefaults={poolDefaults}
          candidateIndex={candidateIndex}
          candidates={candidates}
          onOp={appendOp}
          onDropStagedAdd={dropStagedAdd}
          onUndoRemoveInclusion={undoRemoveInclusion}
          onUndoUpdateInclusion={undoUpdateInclusion}
        />
        <CandidatePicker
          candidates={candidates}
          inclusions={membership.inclusions}
          pendingOps={pendingOps}
          onOp={appendOp}
        />
      </div>

      {/* Stale refinements (not attached to any inclusion card above) */}
      <StaleRefinements
        refinements={membership.refinements}
        staleRefinementIds={staleRefinementIdSet}
        candidateIndex={candidateIndex}
        onOp={appendOp}
      />

      <PushHealthPanel
        poolId={poolId}
        authHeaders={authHeaders}
        apiBaseUrl={apiBaseUrl}
        refreshTrigger={pushHealthRefresh}
        onError={onError}
      />

      <div className={`${styles.actionBar} ${hasPending ? styles.actionBarPending : ''}`}>
        <span className={`${styles.pendingCount} ${hasPending ? '' : styles.pendingCountIdle}`}>
          {hasPending
            ? `${pendingOps.length} pending op${pendingOps.length === 1 ? '' : 's'} — not yet saved`
            : 'No pending changes.'}
        </span>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.actionDiscard}
            onClick={discardAll}
            disabled={!hasPending || saving}
          >
            Discard
          </button>
          <button
            type="button"
            className={styles.actionSave}
            onClick={handleSave}
            disabled={!hasPending || saving}
            title="Apply all pending ops in one transaction and enqueue a single pool push."
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WarmTargetMigrationBanner — one-shot per pool. Surfaces the prelaunch ⇒
// warmTarget rename so operators don't think their warm config got
// dropped. Dismissable via localStorage; banners for many pools coexist
// without bleed-through because the storage key includes the poolId.
// ---------------------------------------------------------------------------

const WARM_TARGET_BANNER_STORAGE_PREFIX = 'astra-warm-target-banner-dismissed:';

function WarmTargetMigrationBanner({ poolId }: { poolId: string }) {
  const storageKey = `${WARM_TARGET_BANNER_STORAGE_PREFIX}${poolId}`;
  const initiallyDismissed = (() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  })();
  const [dismissed, setDismissed] = useState(initiallyDismissed);

  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {
      /* private mode / storage disabled — banner just won't persist */
    }
  };

  return (
    <div className={styles.legacyBanner} role="status">
      <span className={styles.legacyBannerText}>
        Pre-launch is now <strong>warm target</strong> — pick a number (0 = off,
        N = keep N warmed slots ready). Existing settings have been preserved
        (on -&gt; 1, off -&gt; 0). Set explicitly to 0 if you want to disable
        warming for any (app, version).
      </span>
      <button type="button" className={styles.legacyBannerLink} onClick={dismiss}>
        Got it
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PoolDefaultWarmTargetChip — chip-shaped <select> for the pool-default
// warm slot count. Lives in the header next to the precache/multiplex
// chips. The pool default is always an explicit number (not nullable),
// so unlike WarmTargetInput there's no "inherit" option.
//
// Reuses .warmTargetSelect / .warmTargetNativeSelect from
// FlagToggles.module.css for the focus-within ring + transparent overlay
// pattern, so design tweaks land in one place. Two-span label/state
// structure mirrors WarmTargetInput for typographic consistency
// (sans-serif label, mono numeric badge).
// ---------------------------------------------------------------------------

function PoolDefaultWarmTargetChip({
  value,
  onChange,
}: {
  value: number;
  onChange: (next: number) => void;
}) {
  const cls = [
    styles.flagSummaryChip,
    value > 0 ? styles.flagSummaryOn : styles.flagSummaryOff,
    flagStyles.warmTargetSelect,
  ].join(' ');
  const title = value > 0
    ? `Pool default warm target: ${value} warmed slot${value === 1 ? '' : 's'} per (app, version). Auto-enables Precache.`
    : `Pool default warm target: 0 (no warming). Operator can override per inclusion / refinement / version.`;

  const options: number[] = [];
  for (let n = 0; n <= WARM_TARGET_MAX; n++) options.push(n);

  return (
    <label className={cls} title={title}>
      <span className={flagStyles.label} aria-hidden="true">warm</span>
      <span className={flagStyles.state} aria-hidden="true">{value}</span>
      <select
        className={flagStyles.warmTargetNativeSelect}
        value={String(value)}
        aria-label="Pool default warm target"
        onChange={(e) => onChange(Math.max(0, Math.min(WARM_TARGET_MAX, parseInt(e.target.value, 10))))}
      >
        {options.map((n) => (
          <option key={n} value={String(n)}>{n}</option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// StaleRefinements — refinements that no inclusion card will render because
// their parent inclusion is stale or has been removed entirely. Show them in
// a dedicated strip so operators can clean them up.
// ---------------------------------------------------------------------------

function StaleRefinements({
  refinements,
  staleRefinementIds,
  candidateIndex,
  onOp,
}: {
  refinements: PoolRefinement[];
  staleRefinementIds: Set<string>;
  candidateIndex: CandidateIndex;
  onOp: (op: PendingOp) => void;
}) {
  const stale = refinements.filter((r) => staleRefinementIds.has(r.id));
  if (stale.length === 0) return null;
  return (
    <div className={styles.message} style={{ borderColor: 'var(--color-error, #dc2626)', color: 'var(--color-text-primary)' }}>
      <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-2)', color: 'var(--color-error, #dc2626)' }}>
        Stale exceptions ({stale.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', fontSize: 'var(--text-xs)' }}>
        {stale.map((r) => {
          const label = candidateIndex.byKey.get(`${r.nodeKind}:${r.nodeId}`)?.label ?? r.nodeId;
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{r.action}</span>
              <span style={{ fontFamily: 'var(--font-mono)' }}>{r.nodeKind}</span>
              <span style={{ flex: 1 }}>{label}</span>
              <button
                type="button"
                style={{
                  padding: '2px var(--space-2)',
                  fontSize: '10px',
                  fontFamily: 'var(--font-mono)',
                  background: 'transparent',
                  border: 'var(--border-width) solid var(--color-error, #dc2626)',
                  color: 'var(--color-error, #dc2626)',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-wider)',
                  fontWeight: 'var(--weight-bold)',
                }}
                onClick={() => onOp({ kind: 'removeRefinement', id: r.id })}
              >
                Reconfigure
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function tenancyHint(t: PoolTenancy): string {
  if (t === 'single') return 'Only this pool\u2019s project is pickable.';
  if (t === 'organization') return 'Every project in this pool\u2019s org is pickable.';
  return 'Every org platform-wide is pickable.';
}

/**
 * Coalesce consecutive update ops on the same target so the staged log
 * doesn't bloat (e.g., two clicks on precache = one `updateInclusion`
 * with the latest value, not two ops that cancel). Order-preserving.
 */
function coalesceOps(ops: PendingOp[]): PendingOp[] {
  const out: PendingOp[] = [];
  for (const op of ops) {
    const last = out[out.length - 1];
    if (!last) {
      out.push(op);
      continue;
    }
    if (
      op.kind === 'updateInclusion' &&
      last.kind === 'updateInclusion' &&
      last.id === op.id
    ) {
      out[out.length - 1] = { ...last, payload: { ...last.payload, ...op.payload } };
      continue;
    }
    if (
      op.kind === 'updateRefinement' &&
      last.kind === 'updateRefinement' &&
      last.id === op.id
    ) {
      out[out.length - 1] = { ...last, payload: { ...last.payload, ...op.payload } };
      continue;
    }
    if (op.kind === 'updatePoolDefaults' && last.kind === 'updatePoolDefaults') {
      out[out.length - 1] = { ...last, payload: { ...last.payload, ...op.payload } };
      continue;
    }
    out.push(op);
  }
  return out;
}

/**
 * Server receives "staged-<n>" ids as opaque strings but doesn't know what
 * they mean. Collapse update/remove ops targeting local staged ids into
 * their originating add op's payload so the wire payload is clean.
 *
 * Returns a new array; the input is not mutated. Each add op targeted by
 * local update ops is replaced with a fresh add op carrying the folded
 * payload; update/remove ops against local staged ids are dropped.
 */
function foldStagedOps(ops: PendingOp[]): PendingOp[] {
  // Collect staged ids — one per addInclusion / addRefinement op.
  const stagedIdToIndex = new Map<string, number>();
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    if (op.kind === 'addInclusion' || op.kind === 'addRefinement') {
      stagedIdToIndex.set(`staged-${i}`, i);
    }
  }

  // Start by cloning the list so we can replace add ops by index.
  const out: (PendingOp | null)[] = ops.slice();

  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];

    if (op.kind === 'removeInclusion' && stagedIdToIndex.has(op.id)) {
      const target = stagedIdToIndex.get(op.id)!;
      out[target] = null;
      out[i] = null;
      continue;
    }
    if (op.kind === 'removeRefinement' && stagedIdToIndex.has(op.id)) {
      const target = stagedIdToIndex.get(op.id)!;
      out[target] = null;
      out[i] = null;
      continue;
    }
    if (op.kind === 'updateInclusion' && stagedIdToIndex.has(op.id)) {
      const target = stagedIdToIndex.get(op.id)!;
      const addOp = out[target];
      if (addOp && addOp.kind === 'addInclusion') {
        out[target] = {
          kind: 'addInclusion',
          payload: {
            ...addOp.payload,
            ...(op.payload.onlyDefaultVersion !== undefined ? { onlyDefaultVersion: op.payload.onlyDefaultVersion } : {}),
            ...(op.payload.precacheOverride !== undefined ? { precacheOverride: op.payload.precacheOverride } : {}),
            ...(op.payload.prelaunchOverride !== undefined ? { prelaunchOverride: op.payload.prelaunchOverride } : {}),
            ...(op.payload.warmTargetOverride !== undefined ? { warmTargetOverride: op.payload.warmTargetOverride } : {}),
            ...(op.payload.multiplexOverride !== undefined ? { multiplexOverride: op.payload.multiplexOverride } : {}),
          },
        };
      }
      out[i] = null;
      continue;
    }
    if (op.kind === 'updateRefinement' && stagedIdToIndex.has(op.id)) {
      const target = stagedIdToIndex.get(op.id)!;
      const addOp = out[target];
      if (addOp && addOp.kind === 'addRefinement') {
        out[target] = {
          kind: 'addRefinement',
          payload: {
            ...addOp.payload,
            ...(op.payload.precacheOverride !== undefined ? { precacheOverride: op.payload.precacheOverride } : {}),
            ...(op.payload.prelaunchOverride !== undefined ? { prelaunchOverride: op.payload.prelaunchOverride } : {}),
            ...(op.payload.warmTargetOverride !== undefined ? { warmTargetOverride: op.payload.warmTargetOverride } : {}),
            ...(op.payload.multiplexOverride !== undefined ? { multiplexOverride: op.payload.multiplexOverride } : {}),
          },
        };
      }
      out[i] = null;
      continue;
    }
  }

  return out.filter((op): op is PendingOp => op !== null);
}
